import React, { type KeyboardEventHandler, useCallback } from 'react';
import { cx } from '@compiled/react';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives';
import Select, { type ValueType, type StylesConfig } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { OperatorOption, OperatorsSelectProps } from '../../../common/types.tsx';
import messages from './messages.tsx';

const selectStyles: StylesConfig<OperatorOption> = {
	control: (baseStyles) => ({
		...baseStyles,
		backgroundColor: token('color.background.neutral'),
	}),
};

const styles = cssMap({
	operatorsSelectOld: {
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
		paddingTop: token('space.100'),
	},
	operatorsSelect: {
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
		paddingTop: token('space.0'),
		paddingBottom: token('space.100'),
	},
});

const getOptionLabel = (option: OperatorOption): string => option.ariaLabel;
const formatOptionLabel = (option: OperatorOption): string => option.label;

export const OperatorsSelect = ({
	onChange,
	allowedOperators,
	selectedOperator,
}: OperatorsSelectProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const onChangeWithAnalytics = useCallback(
		(option: ValueType<OperatorOption>) => {
			onChange && onChange(option);
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'dropdownItem',
			});
			option &&
				fireUIAnalytics(analyticsEvent, 'operatorSelect', {
					option: option.value,
					selectType: 'dropdown',
				});
		},
		[createAnalyticsEvent, onChange],
	);

	const onMenuOpenAnalytics = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'opened',
			actionSubject: 'dropdown',
		});
		fireUIAnalytics(analyticsEvent, 'operatorSelect', {
			selectType: 'dropdown',
		});
	}, [createAnalyticsEvent]);

	const onKeyDown = useCallback<KeyboardEventHandler<HTMLDivElement>>((event) => {
		event.stopPropagation();
	}, []);

	return (
		<Box
			xcss={cx(
				fg('slv-117-nin-column-picker-style-updates')
					? styles.operatorsSelect
					: styles.operatorsSelectOld,
			)}
		>
			<Select
				aria-label={formatMessage(messages.operatorLabel)}
				value={selectedOperator || allowedOperators[0]}
				options={allowedOperators}
				onChange={onChangeWithAnalytics}
				spacing="compact"
				appearance="subtle"
				styles={selectStyles}
				onKeyDown={onKeyDown}
				onMenuOpen={onMenuOpenAnalytics}
				isSearchable={false}
				getOptionLabel={getOptionLabel}
				formatOptionLabel={formatOptionLabel}
			/>
		</Box>
	);
};
